<div class="gls" (keydown)="onKeydown($event)" role="search" aria-label="Search places and directions">
  <!-- ============================== Search view ============================== -->
  <ng-container *ngIf="mode === 'search'">
    <div class="gls__bar">
      <div class="gls__field">
        <input
          #searchInput
          type="text"
          class="gls__input"
          placeholder="Search place, coordinates or chainage"
          [(ngModel)]="query"
          (keydown.enter)="runSearch()"
          (focus)="inputFocused = true"
          (blur)="inputFocused = false"
          autocomplete="off"
          spellcheck="false"
          aria-label="Search place, coordinates or chainage"
        />
        <button *ngIf="query" type="button" class="gls__clear" (click)="clearQuery()" aria-label="Clear search">×</button>
        <button *ngIf="!query" type="button" class="gls__link" (click)="whereIsThis()" title="Name the place at the map centre">
          Where is this?
        </button>
      </div>
      <button type="button" class="gls__btn gls__btn--primary" (click)="runSearch()" [disabled]="!query.trim() || searching" title="Search" aria-label="Search">
        <span *ngIf="!searching">🔍</span>
        <span *ngIf="searching" class="gls__spinner" aria-hidden="true"></span>
      </button>
      <button type="button" class="gls__btn gls__btn--primary" (click)="openDirections()" title="Directions" aria-label="Directions">↱</button>
      <button type="button" class="gls__btn gls__btn--ghost" (click)="close()" title="Close (Esc)" aria-label="Close">×</button>
    </div>

    <div class="gls__body" *ngIf="searching || searchError || groups.length || searched || showQuickLists">
      <div class="gls__status" *ngIf="searching">Searching…</div>
      <div class="gls__error" *ngIf="!searching && searchError">{{ searchError }}</div>
      <div class="gls__status" *ngIf="!searching && searched && !searchError && !groups.length">No results found</div>

      <section class="gls__group" *ngFor="let group of groups">
        <h4 class="gls__group-title">{{ group.title }}</h4>
        <button
          type="button"
          class="gls__item"
          *ngFor="let place of group.items; trackBy: trackPlace"
          [class.gls__item--active]="selected?.id === place.id"
          (click)="select(place)"
        >
          <span class="gls__item-icon">{{ kindIcon(place) }}</span>
          <span class="gls__item-text">
            <span class="gls__item-label">{{ place.label }}</span>
            <span class="gls__item-sub" *ngIf="place.sublabel">{{ place.sublabel }}</span>
          </span>
        </button>
      </section>

      <ng-container *ngIf="showQuickLists">
        <section class="gls__group" *ngIf="saved.length">
          <h4 class="gls__group-title">Saved places</h4>
          <div class="gls__item gls__item--row" *ngFor="let place of saved; trackBy: trackPlace">
            <ng-container *ngIf="editingSavedId !== place.id; else renameTpl">
              <button type="button" class="gls__item-main" (click)="select(place)">
                <span class="gls__item-icon">★</span>
                <span class="gls__item-text">
                  <span class="gls__item-label">{{ place.label }}</span>
                  <span class="gls__item-sub" *ngIf="place.sublabel">{{ place.sublabel }}</span>
                </span>
              </button>
              <button type="button" class="gls__icon-btn" (click)="startRename(place)" title="Rename" aria-label="Rename">✎</button>
              <button type="button" class="gls__icon-btn" (click)="removeSaved(place)" title="Remove" aria-label="Remove">🗑</button>
            </ng-container>
            <ng-template #renameTpl>
              <input
                class="gls__input gls__input--inline"
                [(ngModel)]="editingLabel"
                (keydown.enter)="commitRename()"
                (blur)="commitRename()"
                aria-label="Place name"
              />
            </ng-template>
          </div>
        </section>

        <section class="gls__group" *ngIf="recent.length">
          <h4 class="gls__group-title">
            Recent
            <button type="button" class="gls__text-btn" (click)="clearRecent()">Clear</button>
          </h4>
          <button type="button" class="gls__item" *ngFor="let place of recent; trackBy: trackPlace" (click)="select(place)">
            <span class="gls__item-icon">🕘</span>
            <span class="gls__item-text">
              <span class="gls__item-label">{{ place.label }}</span>
              <span class="gls__item-sub" *ngIf="place.sublabel">{{ place.sublabel }}</span>
            </span>
          </button>
        </section>
      </ng-container>

      <div class="gls__attribution" *ngIf="groups.length">
        Data © <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a> contributors
      </div>
    </div>
  </ng-container>

  <!-- ============================== Directions view ============================== -->
  <ng-container *ngIf="mode === 'directions'">
    <div class="gls__dir-head">
      <div class="gls__profiles" role="radiogroup" aria-label="Travel mode">
        <button
          type="button"
          *ngFor="let p of profiles"
          class="gls__profile"
          [class.gls__profile--active]="profile === p.id"
          (click)="setProfile(p.id)"
          [title]="p.label"
          [attr.aria-label]="p.label"
          role="radio"
          [attr.aria-checked]="profile === p.id"
        >{{ p.icon }}</button>
      </div>
      <span class="gls__engine" title="Routing engine">OSRM</span>
      <button type="button" class="gls__btn gls__btn--ghost" (click)="backToSearch()" title="Back to search" aria-label="Back to search">×</button>
    </div>

    <div class="gls__endpoints">
      <div class="gls__endpoint-list">
        <div class="gls__endpoint" *ngFor="let which of endpointKeys">
          <span class="gls__endpoint-pin" [class.gls__endpoint-pin--to]="which === 'to'" aria-hidden="true"></span>
          <input
            type="text"
            class="gls__input"
            [placeholder]="which === 'from' ? 'From' : 'To'"
            [attr.aria-label]="which === 'from' ? 'From' : 'To'"
            [(ngModel)]="endpoints[which].text"
            (ngModelChange)="onEndpointEdited(which)"
            (keydown.enter)="searchEndpoint(which)"
            autocomplete="off"
            spellcheck="false"
          />
          <button
            type="button"
            class="gls__icon-btn"
            [class.gls__icon-btn--active]="picking === which"
            (click)="picking === which ? cancelPick() : startPick(which)"
            title="Pick on map"
            aria-label="Pick on map"
          >⌖</button>
          <button type="button" class="gls__icon-btn" (click)="useMyLocation(which)" title="My location" aria-label="My location">◎</button>
        </div>
      </div>
      <button type="button" class="gls__swap" (click)="swap()" title="Swap start and destination" aria-label="Swap start and destination">⇅</button>
    </div>

    <div class="gls__hint" *ngIf="picking">Click the map to set the {{ picking === 'from' ? 'start' : 'destination' }}. Press Esc to cancel.</div>

    <ng-container *ngFor="let which of endpointKeys">
      <div class="gls__body gls__body--flush" *ngIf="activeEndpoint === which && (endpoints[which].loading || endpoints[which].error || endpoints[which].groups.length)">
        <div class="gls__status" *ngIf="endpoints[which].loading">Searching…</div>
        <div class="gls__error" *ngIf="!endpoints[which].loading && endpoints[which].error">{{ endpoints[which].error }}</div>
        <section class="gls__group" *ngFor="let group of endpoints[which].groups">
          <h4 class="gls__group-title">{{ group.title }}</h4>
          <button type="button" class="gls__item" *ngFor="let place of group.items; trackBy: trackPlace" (click)="chooseEndpoint(which, place)">
            <span class="gls__item-icon">{{ kindIcon(place) }}</span>
            <span class="gls__item-text">
              <span class="gls__item-label">{{ place.label }}</span>
              <span class="gls__item-sub" *ngIf="place.sublabel">{{ place.sublabel }}</span>
            </span>
          </button>
        </section>
      </div>
    </ng-container>

    <div class="gls__body" *ngIf="endpoints.from.loading || endpoints.to.loading">
      <div class="gls__status" *ngIf="!activeEndpoint">Finding your location…</div>
    </div>
    <div class="gls__error gls__error--block" *ngIf="!activeEndpoint && (endpoints.from.error || endpoints.to.error)">
      {{ endpoints.from.error || endpoints.to.error }}
    </div>

    <div class="gls__body" *ngIf="routing || routeError || route">
      <div class="gls__status" *ngIf="routing"><span class="gls__spinner" aria-hidden="true"></span> Calculating route…</div>
      <div class="gls__error" *ngIf="!routing && routeError">{{ routeError }}</div>
      <ng-container *ngIf="!routing && route">
        <div class="gls__summary">
          <strong>{{ formatDistance(route.distance_m) }}</strong>
          <span>{{ formatDuration(route.duration_s) }}</span>
        </div>
        <ol class="gls__steps">
          <li *ngFor="let step of route.steps; let i = index">
            <button type="button" class="gls__step" [class.gls__step--active]="activeStep === i" (click)="focusStep(i)">
              <span class="gls__step-text">{{ step.instruction }}</span>
              <span class="gls__step-dist" *ngIf="step.distance_m > 0">{{ formatDistance(step.distance_m) }}</span>
            </button>
          </li>
        </ol>
        <div class="gls__attribution">
          Routing by <a href="https://routing.openstreetmap.de/about.html" target="_blank" rel="noopener">FOSSGIS OSRM</a>, data ©
          <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a> contributors
        </div>
      </ng-container>
    </div>
    <div class="gls__status gls__status--block" *ngIf="!route && !routing && !routeError && !picking && !(endpoints.from.place && endpoints.to.place)">
      Set a start and destination: type a place and press Enter, pick on the map, or use your location. Drag the markers to adjust.
    </div>
  </ng-container>
</div>

<!-- Pin popup (moved into an OpenLayers overlay) -->
<div #pinPopup class="gls-popup" [hidden]="!selected || mode !== 'search'">
  <ng-container *ngIf="selected">
    <button type="button" class="gls-popup__close" (click)="dismissPin()" aria-label="Close">×</button>
    <div class="gls-popup__title">{{ selected.label }}</div>
    <div class="gls-popup__coords">{{ selected.lat | number: '1.5-5' }}, {{ selected.lon | number: '1.5-5' }}</div>
    <div class="gls-popup__actions">
      <button type="button" (click)="openDirections(selected, undefined)">Directions from here</button>
      <button type="button" (click)="openDirections(undefined, selected)">Directions to here</button>
      <button type="button" (click)="toggleSaved(selected)">{{ isSaved(selected) ? '★ Saved' : '☆ Save place' }}</button>
      <button type="button" (click)="copyCoordinates(selected)">{{ copied ? 'Copied' : 'Copy coordinates' }}</button>
    </div>
  </ng-container>
</div>
